import { useCallback, useEffect, useRef, useState } from 'react';
import { courseRunsService } from '../../../services/courseRunsService';
import { jobsService, JobStatus } from '../../../services/jobsService';

export interface LessonBuildWarning {
  stage: string;
  extraction_id?: string | null;
}

export interface LessonBuildState {
  jobId: string;
  status: JobStatus;
  warnings: LessonBuildWarning[];
  error?: string;
}

/**
 * Drives whole-course and per-lesson builds from the lessons dashboard.
 * Tracks the lesson_pipeline_execute job per lesson so the UI can show live
 * status, the warnings[] a build surfaces (skipped stages), and failures.
 * Calls onProgress() each poll so the caller can refresh the lesson list.
 */
export function useCourseBuild(runId: string | undefined, onProgress: () => void) {
  const [jobs, setJobs] = useState<Record<string, LessonBuildState>>({});
  const [error, setError] = useState<string | null>(null);
  const jobsRef = useRef(jobs);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  const building = Object.values(jobs).some(
    (j) => j.status === 'queued' || j.status === 'processing',
  );

  const track = useCallback((entries: Array<{ lessonRunId: string; jobId: string }>) => {
    setJobs((prev) => {
      const next = { ...prev };
      for (const e of entries) next[e.lessonRunId] = { jobId: e.jobId, status: 'queued', warnings: [] };
      return next;
    });
  }, []);

  const buildAll = useCallback(async () => {
    if (!runId) return;
    setError(null);
    try {
      const res = await courseRunsService.buildCourse(runId);
      track(res.jobs.map((j) => ({ lessonRunId: j.lesson_run_id, jobId: j.job_id })));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start build');
    }
  }, [runId, track]);

  const buildLesson = useCallback(async (lessonRunId: string) => {
    setError(null);
    try {
      const res = await courseRunsService.buildLesson(lessonRunId);
      track([{ lessonRunId, jobId: res.job_id }]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to build lesson');
    }
  }, [track]);

  useEffect(() => {
    if (!building) return;
    const id = setInterval(async () => {
      const active = Object.entries(jobsRef.current).filter(
        ([, j]) => j.status === 'queued' || j.status === 'processing',
      );
      await Promise.all(active.map(async ([lessonRunId, j]) => {
        try {
          const rec = await jobsService.get(j.jobId);
          const errStr = rec.error
            ? (typeof rec.error === 'string' ? rec.error : JSON.stringify(rec.error)).slice(0, 200)
            : undefined;
          setJobs((prev) => ({
            ...prev,
            [lessonRunId]: {
              jobId: j.jobId,
              status: rec.status,
              warnings: (rec.result_inline?.warnings as LessonBuildWarning[]) || [],
              error: rec.status === 'failed' ? errStr : undefined,
            },
          }));
        } catch {
          // transient poll failure; retry next tick
        }
      }));
      onProgress();
    }, 8000);
    return () => clearInterval(id);
  }, [building, onProgress]);

  return { jobs, building, error, buildAll, buildLesson };
}
